import React from 'react';
import PropTypes from 'prop-types';
import { Table, Icon, IconButton, Tooltip, Whisper } from 'rsuite';
import moment from 'moment';
import { includes } from 'lodash/fp';
import IconLink from '../../../IconLink';
import {
  reservationsEditPath,
  reservationsViewPath
} from '../../../../routes/Paths';
import redirectTo from '../../../../helpers/redirectTo';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import { COMPLETED, PENDING } from '../../../../constants/reservation/Status';
import {
  createVisitUrl,
  createLinkGuestUrl,
  createReservationUrl
} from '../../../../helpers/reservationUrls';
import {
  CREATE_VISIT as CREATE_VISIT_PERMISSION,
  CREATE_VISIT_PRE_POST as CREATE_VISIT_PRE_POST_PERMISSION
} from '../../../../constants/permissions';
import * as PERMISSIONS from '../../../../constants/permissions/reservation';
import {
  AllowCancelStatus,
  AllowCreateVisitStatus
} from '../../../../constants/reservation/AllowActionSettings';
import {
  modifyAccess,
  fullAccess,
  viewAccess
} from '../../../../helpers/permission';
import AccessControl from '../../../AccessControl';

const { Cell } = Table;
const currentDate = moment().format('YYYY-MM-DD');

const ActionCell = ({
  rowData,
  dataKey,
  handleApprove,
  handleReject,
  handleCancel,
  handleUnlink,
  showApprove,
  showCancel,
  showReject,
  showCreate,
  showVisit,
  isSaving,
  ...props
}) => {
  const { id, guestId, status, requestTime } = rowData;
  const isTodayReservation = moment(requestTime).isSame(currentDate, 'day');
  const isCompleted = status === COMPLETED;
  const createVisitPermissions = !isTodayReservation
    ? [
        modifyAccess(CREATE_VISIT_PERMISSION),
        viewAccess(CREATE_VISIT_PRE_POST_PERMISSION)
      ]
    : [modifyAccess(CREATE_VISIT_PERMISSION)];

  return (
    <Cell {...props} className="link-group">
      {showApprove && (
        <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Approve</Tooltip>}
          >
            <IconButton
              style={{ top: '-8px' }}
              appearance="link"
              color="green"
              onClick={() => {
                handleApprove(rowData);
              }}
              disabled={isSaving || status !== PENDING}
              icon={<Icon icon="thumbs-up" style={{ width: 32 }} />}
            />
          </Whisper>
        </AccessControl>
      )}
      {showReject && (
        <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Reject</Tooltip>}
          >
            <IconButton
              style={{ top: '-8px' }}
              appearance="link"
              color="red"
              onClick={() => {
                handleReject(rowData);
              }}
              disabled={isSaving || status !== PENDING}
              icon={<Icon icon="thumbs-down" />}
            />
          </Whisper>
        </AccessControl>
      )}
      {showVisit && (
        <AccessControl allowWhenAll={createVisitPermissions}>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{isCompleted ? 'View' : 'Create'} Visit</Tooltip>}
          >
            <IconButton
              style={{ top: '-8px' }}
              appearance="link"
              onClick={() => {
                redirectTo(createVisitUrl(rowData), true);
              }}
              disabled={!includes(status, AllowCreateVisitStatus)}
              icon={<Icon icon="shopping-bag" />}
            />
          </Whisper>
        </AccessControl>
      )}
      {showCreate && (
        <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Create Guest Reservation</Tooltip>}
          >
            <IconLink
              icon="calendar-plus-o"
              to={createReservationUrl(rowData)}
            />
          </Whisper>
        </AccessControl>
      )}

      <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
        {!guestId ? (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Link to Guest</Tooltip>}
          >
            <IconLink icon="link" to={createLinkGuestUrl(rowData)} />
          </Whisper>
        ) : (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Unlink Guest</Tooltip>}
          >
            <IconButton
              style={{ top: '-8px' }}
              appearance="link"
              onClick={() => {
                handleUnlink(id, rowData);
              }}
              icon={<Icon icon="unlink" />}
              disabled={isCompleted || isSaving}
            />
          </Whisper>
        )}
      </AccessControl>

      <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.MODULE)}>
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Edit</Tooltip>}
        >
          <IconLink
            icon="edit2"
            disabled={isCompleted}
            to={pagePathToUrl(reservationsEditPath, { id })}
          />
        </Whisper>
      </AccessControl>

      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>View</Tooltip>}
      >
        <IconLink icon="eye" to={pagePathToUrl(reservationsViewPath, { id })} />
      </Whisper>

      {showCancel && (
        <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.MODULE)}>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>Cancel</Tooltip>}
          >
            <IconButton
              style={{ top: '-8px' }}
              appearance="link"
              color="red"
              onClick={() => {
                handleCancel(rowData);
              }}
              disabled={isSaving || !includes(status, AllowCancelStatus)}
              icon={<Icon icon="close-circle" />}
            />
          </Whisper>
        </AccessControl>
      )}
    </Cell>
  );
};

ActionCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.number,
    outreachId: PropTypes.number,
    guestId: PropTypes.number,
    status: PropTypes.number,
    requestTime: PropTypes.string,
    programs: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  dataKey: PropTypes.string.isRequired,
  handleApprove: PropTypes.func,
  handleReject: PropTypes.func,
  handleCancel: PropTypes.func,
  handleUnlink: PropTypes.func,
  showApprove: PropTypes.bool,
  showReject: PropTypes.bool,
  showCancel: PropTypes.bool,
  showVisit: PropTypes.bool,
  showCreate: PropTypes.bool,
  isSaving: PropTypes.bool
};

ActionCell.defaultProps = {
  rowData: null,
  handleApprove: () => {},
  handleReject: () => {},
  handleCancel: () => {},
  handleUnlink: () => {},
  showApprove: false,
  showReject: false,
  showCancel: false,
  showVisit: false,
  showCreate: false,
  isSaving: false
};

export default ActionCell;
